import Close from "@mui/icons-material/Close";
import {
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import type { IncidentWorkItemId } from "@thalamos/vision-common";

import { apiHooks } from "../../api/index.js";
import { PopupDialog } from "../../components/Dialog/PopupDialog.js";
import { MultiPagePdf } from "../../components/index.js";
import { Error as ErrorComponent } from "../../pages/Error/Error.js";

interface PdfViewerModalInnerProps {
  open: boolean;
  onClose: () => void;
  pdfData: string | null;
}

interface PdfViewerModalProps {
  open: boolean;
  onClose: () => void;
  incidentWorkItemId: IncidentWorkItemId;
}

export function PdfViewerModalInner({
  open,
  onClose,
  pdfData,
}: PdfViewerModalInnerProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <PopupDialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={fullScreen}
      disableDialogRootPadding={true}
    >
      <IconButton
        aria-label="close"
        onClick={onClose}
        edge="end"
        sx={{
          position: "absolute",
          right: 15,
          top: 8,
        }}
      >
        <Close />
      </IconButton>
      {pdfData ? (
        <>
          <DialogContent>
            <MultiPagePdf data={pdfData} />
          </DialogContent>
          <DialogActions
            sx={{
              display: "flex",
              justifyContent: "space-between",
              px: 3,
            }}
          >
            <Button onClick={onClose} variant="outlined">
              {t("common.close")}
            </Button>
          </DialogActions>
        </>
      ) : (
        <CircularProgress />
      )}
    </PopupDialog>
  );
}

export function PdfViewerModal({
  open,
  onClose,
  incidentWorkItemId,
}: PdfViewerModalProps) {
  const [{ data: pdfData = null, error }] =
    apiHooks.incidentWorkItem.getPdf(incidentWorkItemId);

  if (error) return <ErrorComponent />;

  return (
    <PdfViewerModalInner open={open} onClose={onClose} pdfData={pdfData} />
  );
}
